import React, { FC, useEffect, useReducer } from 'react';

import { CloudAccountGetResponse } from '@ariksa/cloud-account/api';
import { NativeResources } from '@ariksa/inventory-core';
import { InsightV2ApiInsightV2AllRequest } from '@ariksa/inventory-core/api';
import { HStack } from '@chakra-ui/react';
import { map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useDataScanningService } from 'services/DataScanning';
import { useInventoryInsightService } from 'services/Inventory/useInventoryInsightService';

import { getIcon, StackedHeader, WarningAlert } from 'components/DataDisplay';
import {
  CancelButton,
  OutlinedForm,
  PrimaryButton,
} from 'components/DataEntry';
import { IconTypes, MaskIcon } from 'components/index';
import { Modal } from 'components/Overlay';
import { EnableZeroTrustAccessConfirmationModal } from 'containers/Jit/MaskingPolicies/Components/EnableZeroTrustAccess/EnableZeroTrustAccessConfirmationModal';
import { seletMaskingPolicies } from 'containers/Jit/MaskingPolicies/selectors';
import { actions } from 'containers/Jit/MaskingPolicies/slice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  accounts: CloudAccountGetResponse[];
}

const defaultMaskOptions = [{ label: 'Full Mask', value: 'FULL_MASK' }];

interface LocalState {
  selectedAccount?: Record<string, any>;
  selectedRoles: Record<string, any>[];
  selectedMask: typeof defaultMaskOptions[number];
  isConfirmationOpen: boolean;
  accountOptions: Record<string, any>[];
  dataTypeOptions: Record<string, any>[];
  selectedDataTypes: Record<string, any>[];
  roleOptions: Record<string, any>[];
}

export const EnableZeroTrustAccessModal: FC<Props> = props => {
  const { isOpen, onClose, accounts } = props;
  const dispatch = useDispatch();
  const { baseMaskPolicyAction, dataPolicies } = useSelector(
    seletMaskingPolicies,
  );
  const { inventoryInsights } = useInventoryInsightService();
  const { sensitiveDataInfoTable } = useDataScanningService();
  const syncInventoryInsights = inventoryInsights.sync;
  const syncSensitiveDataInfo = sensitiveDataInfoTable.sync;

  // Keep form selections and the confirmation step in one local state object.
  const [state, updateState] = useReducer(
    (previous: LocalState, next: Partial<LocalState>) => ({
      ...previous,
      ...next,
    }),
    {
      selectedAccount: {},
      selectedRoles: [],
      selectedMask: defaultMaskOptions[0],
      isConfirmationOpen: false,
      accountOptions: [],
      dataTypeOptions: [],
      selectedDataTypes: [],
      roleOptions: [],
    },
  );

  // set account options
  useEffect(() => {
    let options = map(accounts, account => ({
      label: account.name ?? account.cloud_account_id ?? account.uuid,
      value: account.uuid,
      data: account,
      icon: getIcon(account?.cloud_type!),
    }));

    updateState({ accountOptions: options, selectedAccount: options?.[0] });
  }, [accounts]);

  // Roles and tags are scoped to the currently selected Snowflake account.
  useEffect(() => {
    const accountId = state.selectedAccount?.value;
    if (!accountId) return;

    const payload: InsightV2ApiInsightV2AllRequest = {
      insightV2RequestBase: {
        resource_type: NativeResources.SnowflakeRoles,
        account_id: [accountId],
      },
    };

    syncInventoryInsights(payload);
    syncSensitiveDataInfo({
      accountIds: [accountId],
      page: 1,
      size: 100,
    });
  }, [
    isOpen,
    syncInventoryInsights,
    syncSensitiveDataInfo,
    state.selectedAccount?.value,
  ]);

  // Convert the latest API responses into values used by the form controls.
  useEffect(() => {
    const dataTypeOptions = map(sensitiveDataInfoTable.data?.items, o => ({
      label: o?.name,
      value: 'ARIKSA_GOVERNANCE.DATA.' + o?.name,
      isDisabled: true,
    }));

    updateState({
      dataTypeOptions,
      selectedDataTypes: dataTypeOptions,
    });
  }, [sensitiveDataInfoTable.data]);

  useEffect(() => {
    updateState({
      roleOptions: map(inventoryInsights.data?.items ?? [], role => ({
        label: role?.name ?? role?.resource_id,
        value: role?.resource_id ?? role?.name,
        data: role,
        icon: getIcon(IconTypes.Role),
      })),
    });
  }, [inventoryInsights.data]);

  // Deploy one masking policy per tag after the confirmation step succeeds.
  const handleSubmit = () => {
    if (!state.selectedAccount?.value || !state.selectedDataTypes.length)
      return;

    updateState({ isConfirmationOpen: false });

    dispatch(
      actions.createAndDeployBaseMaskPolicies({
        q: {
          baseMaskCreateAndDeployRequest: {
            account_id: state.selectedAccount.value,
            tag_keys: map(state.selectedDataTypes, dataType => dataType.value),
            no_mask_roles: map(state.selectedRoles, role => role?.value),
          },
        },
        onSuccess: () => {
          onClose();
          dispatch(
            actions.getDataPolicies({
              q: {
                page: dataPolicies.page.info.page_number,
                size: dataPolicies.page.info.page_size,
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        header={
          <StackedHeader
            upper="Enable Zero Trust Access"
            lower="Set a default mask for all roles to restrict default access"
            icon={<MaskIcon />}
            color="primary"
            bg="purple.100"
            useCustomColor
            style={{ p: 1.5 }}
          />
        }
        styles={{
          content: { maxW: '620px' },
          body: { px: 8, pb: 8 },
        }}
        footer={
          <HStack justify="space-between" w="full">
            <CancelButton onClick={onClose} />
            <PrimaryButton
              isLoading={baseMaskPolicyAction.isLoading}
              isDisabled={
                inventoryInsights.isLoading ||
                sensitiveDataInfoTable.isLoading ||
                baseMaskPolicyAction.isLoading ||
                !state.selectedDataTypes.length
              }
              px={6}
              onClick={() => updateState({ isConfirmationOpen: true })}
            >
              OK
            </PrimaryButton>
          </HStack>
        }
        body={
          <OutlinedForm
            fields={[
              {
                type: 'select',
                name: 'account',
                label: 'Account',
                options: state.accountOptions,
                value: state.selectedAccount,
                onChange: option => {
                  updateState({
                    selectedAccount: option,
                    selectedRoles: [],
                    selectedDataTypes: [],
                  });
                },
              },
              {
                type: 'display',
                name: 'cloudId',
                label: 'Cloud ID',
                value: state.selectedAccount?.data?.cloud_account_id ?? '-',
                styles: { value: { pt: 2 } },
              },
              {
                type: 'select',
                name: 'defaultMask',
                label: 'Default Mask',
                options: defaultMaskOptions,
                value: state.selectedMask,
                onChange: selectedMask => updateState({ selectedMask }),
                styles: { skeleton: { gridColumn: '1 / -1' } },
              },
              {
                type: 'select',
                name: 'dataTypes',
                label: 'Data Types Detected in Data Sources',
                options: state.dataTypeOptions,
                value: state.selectedDataTypes,
                // onChange: selectedDataTypes =>
                //   updateState({ selectedDataTypes }),
                styles: { skeleton: { gridColumn: '1 / -1' } },
                selectProps: {
                  isMulti: true,
                  showTotalSelected: true,
                  showClearIndicator: false,
                  isLoading: sensitiveDataInfoTable.isLoading,
                },
              },
              {
                type: 'select',
                name: 'rolesToExclude',
                label: 'Roles To Exclude',
                options: state.roleOptions,
                value: state.selectedRoles,
                placeholder: 'Select roles',
                onChange: selectedRoles => updateState({ selectedRoles }),
                styles: { skeleton: { gridColumn: '1 / -1' } },
                selectProps: {
                  isMulti: true,
                  showTotalSelected: true,
                  showSelectedAtBottom: true,
                  showSelectedAtBottomOutside: true,
                  showClearIndicator: true,
                  isLoading: inventoryInsights.isLoading,
                },
              },
              {
                type: 'custom',
                name: 'zeroTrustWarning',
                styles: { skeleton: { gridColumn: '1 / -1' } },
                component: (
                  <WarningAlert message="Ariksa has identified the following sensitive data types in your data sources. Default mask will be applied for access to all of these" />
                ),
              },
            ]}
            styles={{
              fields: {
                display: 'grid',
                gridTemplateColumns: {
                  base: 'minmax(0, 1fr)',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 5,
                spacing: 0,
              },
            }}
          />
        }
      />
      {!!state.isConfirmationOpen && (
        <EnableZeroTrustAccessConfirmationModal
          isOpen={state.isConfirmationOpen}
          onClose={() => updateState({ isConfirmationOpen: false })}
          handleSubmit={handleSubmit}
          isLoading={baseMaskPolicyAction.isLoading}
        />
      )}
    </>
  );
};
